import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { palette } from '$tablet-app/src/app/theme';
import { addMonths } from '$tablet-app/src/app/utils/dateTime';
import useCurrentDay from '$tablet-app/src/app/utils/useCurrentDay';
import { format, isSameDay, isSameMonth } from 'date-fns';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { getPaddedWeeksOfMonth } from '../../../utils/dateTime';

interface Props {
  /** Date to show month for */
  focus_date_string?: string;

  /** Date that is selected */
  selected_date_string?: string;

  onPressDay?: (day: string) => void;
}

const weekday_headings = ['Sun', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarMonthPicker: FC<Props> = (props) => {
  const { focus_date_string, selected_date_string, onPressDay } = props;

  const today = useCurrentDay();

  const selected_date = selected_date_string && new Date(selected_date_string);

  const [month_date_string, setMonthDateString] = useState<string>(
    focus_date_string || new Date().toISOString()
  );

  const weeks = useMemo(
    () => getPaddedWeeksOfMonth(new Date(month_date_string)),
    [month_date_string]
  );

  /** Increments the month */
  const goNextMonth = () =>
    setMonthDateString(addMonths(new Date(month_date_string), 1).toISOString());

  /** Decrements the month */
  const goPreviousMonth = () =>
    setMonthDateString(
      addMonths(new Date(month_date_string), -1).toISOString()
    );

  /** Allows for the month to be set from external component */
  useEffect(() => {
    setMonthDateString(focus_date_string || new Date().toISOString());
  }, [focus_date_string]);

  return (
    <View>
      <Row justifyContent="space-between" alignItems="center">
        <Text variant="h3" weight="semibold">
          {format(new Date(month_date_string), 'MMMM yyyy')}
        </Text>

        <Row>
          <NewButton
            variant="secondary"
            icon="chevron-left"
            onPress={goPreviousMonth}
            testID="previous-month-button"
          />
          <Spacer width="12px" />
          <NewButton
            variant="secondary"
            icon="chevron-right"
            onPress={goNextMonth}
            testID="next-month-button"
          />
        </Row>
      </Row>

      <Spacer height="18px" />

      {/* Week headers */}
      <Row justifyContent="space-between">
        {weekday_headings.map((d) => (
          <DayCell key={d}>
            <Text weight="semibold" variant="caption">
              {d}
            </Text>
          </DayCell>
        ))}
      </Row>

      {/* Days */}
      {weeks.map((week, index) => (
        <Row
          key={'week-' + index}
          paddingVertical={5}
          alignItems="center"
          justifyContent="space-between"
        >
          {week.map((day, idx) =>
            day ? (
              <WeekDayCell
                key={idx}
                day={day}
                active={!!selected_date && isSameDay(day, selected_date)}
                today={isSameDay(day, new Date(today))}
                other_month={!isSameMonth(day, new Date(month_date_string))}
                onPress={onPressDay}
              />
            ) : null
          )}
        </Row>
      ))}
    </View>
  );
};

interface DayCellProps {
  day: Date;
  active?: boolean;
  today?: boolean;
  other_month?: boolean;

  onPress?: (day: string) => void;
}

const WeekDayCell: FC<DayCellProps> = ({
  day,
  active,
  today,
  other_month,
  onPress,
}) => (
  <DayCell
    active={active}
    today={today}
    onPress={() => onPress?.(day.toISOString())}
  >
    <DayCellText
      variant="caption"
      weight={other_month ? 'regular' : 'semibold'}
      active={active}
      today={today}
      other_month={other_month}
    >
      {format(day, 'd')}
    </DayCellText>
  </DayCell>
);

const DayCell = styled.TouchableOpacity<{ today?: boolean; active?: boolean }>`
  width: 47px;
  height: 47px;
  border-radius: 15px;

  align-items: center;
  justify-content: center;

  ${({ today, active }) => {
    return `background-color: ${
      today ? palette.primary_70 : active ? palette.primary_20 : 'white'
    }`;
  }};
`;

const DayCellText = styled(Text)<{
  today?: boolean;
  active?: boolean;
  other_month?: boolean;
}>`
  ${({ today, active, other_month }) => {
    return `${
      today
        ? 'color: white;'
        : active
        ? `color: ${palette.primary_70};`
        : other_month
        ? `color: rgba(116, 116, 116, 1)`
        : ''
    }`;
  }}
`;

export default CalendarMonthPicker;
